// =============================================================================
// app/src/lib/zonas.ts — qual zona da loja (Z1…Z5) guarda cada peça.
//
// A zona mora no COMEÇO da localização que a peça já tem (stock.local): a
// peça em "Z3-G-1-4" está na Z3, na posição G-1-4 de lá. É o mesmo casamento
// por prefixo do mapa 2D (lib/mapa.ts, padrão D7: corredor-prateleira-gaveta),
// com a zona como primeiro nível. Por que não uma coluna nova: a etiqueta que
// já está na prateleira continua valendo, e nada no banco precisou mudar.
// =============================================================================
import { zonaDoEndereco } from './enderecos';
import { CODIGOS_DE_ZONA, zonaDoObjeto, type CodigoZona, type Objeto } from './loja3d';
import { zonaCasaComLocal } from './mapa';

/**
 * A zona em que a localização começa ("Z3-…"), ou a da ÁREA do endereço na
 * regra da loja (ES-CO-… está na Z2) — ou null ("loja", "G-1-4", vazio).
 */
export function zonaDoLocal(local: string | null | undefined): CodigoZona | null {
  if (!local) return null;
  return CODIGOS_DE_ZONA.find((z) => zonaCasaComLocal(z, local)) ?? zonaDoEndereco(local);
}

export interface Endereco {
  zona: CodigoZona | null;
  /** O objeto do mapa (prateleira, gancho, calota…) cujo código casa com a localização. */
  objeto: Objeto | null;
}

/**
 * Onde a peça mora. Primeiro o OBJETO de código mais longo que casa com a
 * localização — é o que deixa as etiquetas antigas valerem: uma prateleira
 * com o código "PR-FR-D" recebe as peças "PR-FR-D-03" sem mexer no cadastro,
 * e a zona vem de onde a prateleira está. Sem objeto, vale a zona escrita na
 * frente da localização ("Z3-…").
 */
export function ondeMora(local: string | null | undefined, objetos: readonly Objeto[]): Endereco {
  if (!local?.trim()) return { zona: null, objeto: null };
  let melhor: Objeto | null = null;
  for (const o of objetos) {
    if (!o.codigo || !zonaCasaComLocal(o.codigo, local)) continue;
    if (!melhor || o.codigo.length > (melhor.codigo?.length ?? 0)) melhor = o;
  }
  if (melhor) return { zona: zonaDoObjeto(melhor), objeto: melhor };
  return { zona: zonaDoLocal(local), objeto: null };
}

// "Z3", "Z3-…", "z3 - …" — a zona só conta se o nível termina ali ("Z30" não é Z3).
const PREFIXO_DE_ZONA = /^\s*Z[1-5](?=$|[\s-])[\s-]*/i;

/**
 * A localização depois de mudar a peça de zona: troca a zona que houver, ou
 * põe a nova na frente. O resto fica como foi digitado — "G-1-4" vira
 * "Z3-G-1-4", "Z1-loja" vira "Z3-loja", e peça sem localização fica só "Z3".
 */
export function localNaZona(local: string | null | undefined, zona: CodigoZona): string {
  const resto = (local ?? '').replace(PREFIXO_DE_ZONA, '').trim();
  return resto ? `${zona}-${resto}` : zona;
}

/**
 * O "Mudar de zona…" ainda serve para esta localização? Só para endereço
 * VELHO ("G-1-4", "Z1-CAL07", "loja", vazio): a zona entra na frente e a peça
 * ganha uma zona. Endereço na regra TIPO-ÁREA ("PR-FR-V1-01-05") já diz a zona
 * pela área, e o "Mudar de zona" o transformaria em "Z3-PR-FR-V1-01-05",
 * tirando a peça da vaga sem evento (desenho "Onde fica", 06/10/2026). Quem
 * muda esse de lugar é o "Preencher"/"Tirar daqui", pelas RPCs do endereço.
 */
export function podeMudarDeZona(local: string | null | undefined): boolean {
  return zonaDoEndereco(local) === null;
}

/** Chave para agrupar localizações: "loja" e "LOJA " são a mesma prateleira. */
export const chaveDoLocal = (local: string | null | undefined): string =>
  (local ?? '').trim().toUpperCase();

export interface ComLocal {
  local: string | null;
}

export interface GrupoDeLocal<T extends ComLocal> {
  chave: string;
  /** Como aparece na tela: a primeira grafia encontrada, ou "(sem localização)". */
  rotulo: string;
  pecas: T[];
}

/** Agrupa por localização, na ordem alfabética da chave (sem localização por último). */
export function agruparPorLocal<T extends ComLocal>(pecas: readonly T[]): GrupoDeLocal<T>[] {
  const grupos = new Map<string, GrupoDeLocal<T>>();
  for (const p of pecas) {
    const chave = chaveDoLocal(p.local);
    let g = grupos.get(chave);
    if (!g) {
      g = { chave, rotulo: p.local?.trim() || '(sem localização)', pecas: [] };
      grupos.set(chave, g);
    }
    g.pecas.push(p);
  }
  return [...grupos.values()].sort((a, b) => {
    if (!a.chave) return 1;
    if (!b.chave) return -1;
    return a.chave.localeCompare(b.chave, 'pt-BR', { numeric: true });
  });
}

/** As peças separadas por zona; o que não mora em zona nenhuma fica em `semZona`. */
export function separarPorZona<T extends ComLocal>(pecas: readonly T[], objetos: readonly Objeto[] = []): {
  porZona: Record<CodigoZona, T[]>;
  semZona: T[];
} {
  const porZona = Object.fromEntries(CODIGOS_DE_ZONA.map((z) => [z, [] as T[]])) as Record<CodigoZona, T[]>;
  const semZona: T[] = [];
  for (const p of pecas) {
    const z = ondeMora(p.local, objetos).zona;
    if (z) porZona[z].push(p);
    else semZona.push(p);
  }
  return { porZona, semZona };
}

export interface GrupoDeLugar<T extends ComLocal> extends GrupoDeLocal<T> {
  /** O objeto do mapa onde essas peças moram, quando algum código casa. */
  objeto: Objeto | null;
}

/**
 * As peças de uma zona agrupadas pelo LUGAR: primeiro as que moram num objeto
 * do mapa (na ordem do código), depois as que só têm a localização escrita.
 */
export function agruparPorLugar<T extends ComLocal>(pecas: readonly T[], objetos: readonly Objeto[]): GrupoDeLugar<T>[] {
  const noObjeto = new Map<string, GrupoDeLugar<T>>();
  const soLocal: T[] = [];
  for (const p of pecas) {
    const { objeto } = ondeMora(p.local, objetos);
    if (!objeto) { soLocal.push(p); continue; }
    let g = noObjeto.get(objeto.origem);
    if (!g) {
      g = { chave: `obj:${objeto.origem}`, rotulo: `${objeto.nome ?? 'Lugar'} · ${objeto.codigo}`, pecas: [], objeto };
      noObjeto.set(objeto.origem, g);
    }
    g.pecas.push(p);
  }
  const porObjeto = [...noObjeto.values()].sort((a, b) =>
    (a.objeto?.codigo ?? '').localeCompare(b.objeto?.codigo ?? '', 'pt-BR', { numeric: true }));
  return [...porObjeto, ...agruparPorLocal(soLocal).map((g) => ({ ...g, objeto: null }))];
}

/** "Lanterna 3 · Retrovisor 2" — as categorias que MORAM na zona, das mais comuns. */
export function categoriasDaZona(pecas: ReadonlyArray<{ categoria: string | null }>): Array<[string, number]> {
  const conta = new Map<string, number>();
  for (const p of pecas) {
    const c = p.categoria?.trim();
    if (c) conta.set(c, (conta.get(c) ?? 0) + 1);
  }
  return [...conta.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'pt-BR'));
}

/** Para a busca da tela: casa descrição, SKU, marca ou localização, sem acento nem caixa. */
export function casaComBusca(
  p: { sku: string; descricao: string; marca: string | null; local: string | null },
  termo: string,
): boolean {
  const limpo = semAcento(termo.trim());
  if (!limpo) return true;
  const alvo = semAcento([p.descricao, p.sku, p.marca ?? '', p.local ?? ''].join(' '));
  return limpo.split(/\s+/).every((palavra) => alvo.includes(palavra));
}

// Os acentos, depois do NFD, são as marcas combinantes de U+0300 a U+036F.
const ACENTOS = new RegExp(`[${String.fromCharCode(0x300)}-${String.fromCharCode(0x36f)}]`, 'g');
const semAcento = (s: string) => s.normalize('NFD').replace(ACENTOS, '').toLowerCase();
